import React, { useRef, useState, useEffect } from "react";
import { Box, OutlinedInput, TextField, Typography, useTheme } from "@mui/material";
import { useDarkTheme } from "../../utils/useDarkTheme";

export default function LoginOtpGroupedField({
  value,
  onChange,
  length = 6,
  error,
  helperText,
  primaryColor,
  fontFamily,
  onOtpInputStart,
  onOtpPaste,
  handleOtpKeyPress,
  customDetails,
  autoFocus = false,
  focusKey,
}) {
  const inputsRef = useRef([]);
  const theme = useTheme();
  const isDark = useDarkTheme(customDetails);
  // tracks which index is temporarily revealed after entry
  const [revealedIndex, setRevealedIndex] = useState(null);
  const revealTimerRef = useRef(null);

  const revealAndMask = (idx) => {
    clearTimeout(revealTimerRef.current);
    setRevealedIndex(idx);
    revealTimerRef.current = setTimeout(() => setRevealedIndex(null), 800);
  };

  useEffect(() => () => clearTimeout(revealTimerRef.current), []);

  useEffect(() => {
    if (!autoFocus) return undefined;
    const timer = setTimeout(() => inputsRef.current[0]?.focus(), 50);
    return () => clearTimeout(timer);
  }, [autoFocus, focusKey, length]);

  const handleChange = (e, index) => {
    const val = e.target.value.replace(/\D/, ""); // only digits
    if (!val) return;
  
    const otpArray = value.split("").concat(Array(length).fill("")).slice(0, length);
  
    // find the first empty position from the start
    const firstEmptyIndex = otpArray.findIndex((digit) => !digit);
  
    // decide where to actually put the value
    const targetIndex =
      firstEmptyIndex !== -1 && index > firstEmptyIndex
        ? firstEmptyIndex
        : index;
  
    otpArray[targetIndex] = val[0];
    const newOtp = otpArray.join("");
    onChange(newOtp);
    revealAndMask(targetIndex);
  
    // focus the next field
    if (targetIndex < length - 1) {
      inputsRef.current[targetIndex + 1]?.focus();
    }
  };
  

  const handleKeyDown = (e, index) => {
    const keysToPrevent = ["PageUp", "PageDown", "ArrowUp", "ArrowDown"];
    if (keysToPrevent.includes(e.key)) {
      e.preventDefault();
    }

    if (e.key === "Enter") {
      e.preventDefault();
      e.target.closest("form")?.requestSubmit();
      return;
    }

    if (e.key === "Backspace") {
      e.preventDefault();
      const otpArray = value.split("");
  
      if (otpArray[index]) {
        // If current box has a value, clear it
        otpArray[index] = "";
        onChange(otpArray.join(""));
      } else if (index > 0) {
        // If empty, move focus to previous and clear that
        inputsRef.current[index - 1]?.focus();
        otpArray[index - 1] = "";
        onChange(otpArray.join(""));
      }
    }
  };

  const handleFocus = (e) => {
    onOtpInputStart?.("focus");

    e.target.addEventListener(
      "wheel",
      function (evt) {
        evt.preventDefault();
      },
      { passive: false }
    );
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, length);
    onChange(pasted);
    inputsRef.current[Math.min(pasted.length, length) - 1]?.focus();
    onOtpPaste?.(pasted);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%" }}>
      <Box sx={{ display: "flex", gap: "0.5rem", width: "100%", maxWidth: "340px" }}>
        {Array.from({ length }).map((_, idx) => (
          <TextField
            key={idx}
            value={value[idx] ? (revealedIndex === idx ? value[idx] : "*") : ""}
            placeholder="—"
            onChange={(e) => handleChange(e, idx)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            onFocus={handleFocus}
            onPaste={handlePaste}
            inputProps={{
              maxLength: 1,
              inputMode: "numeric",
              onKeyPress: handleOtpKeyPress,
            }}
            sx={{
              flex: 1,
              minWidth: 0,
              maxWidth: '52px',
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderRadius: "8px",
                },
                "& input": {
                  color: isDark ? "#FFFFFF" : "#000000",
                  textAlign: "center",
                  fontSize: "1.5rem",
                },
                "& input::placeholder": {
                  color: isDark ? "#555555" : "#C4C4C4",
                  opacity: 1,
                  textAlign: "center",
                  fontSize: "1rem",
                  lineHeight: "1",
                },
                "&:hover fieldset": {
                  borderColor: primaryColor,
                },
                "&.Mui-focused fieldset": {
                  borderColor: primaryColor,
                  borderWidth: "2px",
                },
                "&.Mui-error fieldset": {
                  borderColor: "error.main",
                },
              },
              "& .MuiInputBase-input": {
                color: isDark ? "#FFFFFF" : "#000000",
                WebkitTextFillColor: isDark ? "#FFFFFF" : "#000000",
                caretColor: isDark ? "#FFFFFF" : "#000000",
                padding: {
                  xs: "16.5px 0",
                  md: "12.5px 0"
                }
              },
              borderRadius: "0px !important",
              fontFamily
            }}
            error={error}
            inputRef={(el) => (inputsRef.current[idx] = el)}
          />
        ))}
      </Box>
      {error && helperText && (
        <Typography
          color="error"
          fontSize="0.75rem"
          sx={{ padding: 0, mt: "4px", lineHeight: 1 }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
}
